'use client';

/**
 * ESPACE D'IDENTIFICATION ROYAL — espace membre NDIAMSYL PEYE.
 * Carte d'identification (nom, email, ville → persistée) puis tableau de bord :
 * rang royal, planificateur de Khatma, tracker Hifz (30 juz), Quiz Royal,
 * progression (versets lus/écoutés, favoris), accès rapide aux autres outils.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  useApp, loadHifzJuz, saveHifzJuz, loadBestQuiz, saveBestQuiz, type Member,
} from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import PageBanner from './PageBanner';
import { cn } from '@/lib/utils';
import {
  ShieldCheck, LogOut, Crown, Medal, Award, BookOpenCheck, Brain, Target,
  Sparkles, Compass, CalendarDays, Headphones, Video, Globe2, Check, ChevronRight,
} from 'lucide-react';

/* ===== Quiz Royal — 8 questions ===== */
interface QuizQ { q: { fr: string; en: string }; options: { fr: string; en: string }[]; answer: number }
const QUIZ: QuizQ[] = [
  {
    q: { fr: 'Combien la sourate Al-Fatiha compte-t-elle de versets ?', en: 'How many verses does Surah Al-Fatiha have?' },
    options: [{ fr: '5', en: '5' }, { fr: '7', en: '7' }, { fr: '9', en: '9' }], answer: 1,
  },
  {
    q: { fr: 'Quelle sourate est la plus longue du Coran ?', en: 'Which surah is the longest of the Quran?' },
    options: [{ fr: 'Yā-Sīn', en: 'Yā-Sīn' }, { fr: 'Āl ʿImrān', en: 'Āl ʿImrān' }, { fr: 'Al-Baqara', en: 'Al-Baqara' }], answer: 2,
  },
  {
    q: { fr: 'La révélation coranique a duré environ…', en: 'The Quranic revelation lasted about…' },
    options: [{ fr: '10 ans', en: '10 years' }, { fr: '23 ans', en: '23 years' }, { fr: '40 ans', en: '40 years' }], answer: 1,
  },
  {
    q: { fr: 'Combien de sourates sont médinoises ?', en: 'How many surahs are Medinan?' },
    options: [{ fr: '28', en: '28' }, { fr: '86', en: '86' }, { fr: '50', en: '50' }], answer: 0,
  },
  {
    q: { fr: 'Le verset de l\u2019expansion de l\u2019univers se trouve dans la sourate…', en: 'The verse on the expansion of the universe is in surah…' },
    options: [{ fr: 'Adh-Dhāriyāt (51)', en: 'Adh-Dhāriyāt (51)' }, { fr: 'Al-Kahf (18)', en: 'Al-Kahf (18)' }, { fr: 'Yā-Sīn (36)', en: 'Yā-Sīn (36)' }], answer: 0,
  },
  {
    q: { fr: 'Quelle science étudie les causes de la révélation ?', en: 'Which science studies the occasions of revelation?' },
    options: [{ fr: 'Tajwīd', en: 'Tajwīd' }, { fr: 'Asbāb an-Nuzūl', en: 'Asbāb an-Nuzūl' }, { fr: 'Qirāʾāt', en: 'Qirāʾāt' }], answer: 1,
  },
  {
    q: { fr: 'La sourate 57 s\u2019intitule…', en: 'Surah 57 is named…' },
    options: [{ fr: 'An-Nūr (La Lumière)', en: 'An-Nūr (The Light)' }, { fr: 'Al-Ḥadīd (Le Fer)', en: 'Al-Ḥadīd (Iron)' }, { fr: 'Ar-Raḥmān', en: 'Ar-Raḥmān' }], answer: 1,
  },
  {
    q: { fr: 'Combien de juz (parties) compte le Mushaf ?', en: 'How many juz (parts) does the Mushaf have?' },
    options: [{ fr: '20', en: '20' }, { fr: '40', en: '40' }, { fr: '30', en: '30' }], answer: 2,
  },
];

const TIERS = ['silver', 'gold', 'royal'] as const;

export default function EspaceSection() {
  const { uiLang, member, signIn, signOut, readSet, listenedSet, bookmarks } = useApp();
  const t = DICTS[uiLang];
  const bl = uiLang === 'ar' ? 'fr' : uiLang;

  /* ===== Formulaires d'identification ===== */
  const [fName, setFName] = useState('');
  const [fEmail, setFEmail] = useState('');
  const [fCity, setFCity] = useState('');

  /* ===== Outils ===== */
  const [juz, setJuz] = useState<Record<string, boolean>>({});
  const [khatmaPerDay, setKhatmaPerDay] = useState(20);
  const [quizIdx, setQuizIdx] = useState(0);
  const [quizPick, setQuizPick] = useState<number | null>(null);
  const [quizScore, setQuizScore] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [bestQuiz, setBestQuiz] = useState(0);
  const [hydrated, setHydrated] = useState(false);

  // Hydratation locale (ajustement d'état pendant le rendu)
  if (!hydrated) {
    setHydrated(true);
    setJuz(loadHifzJuz());
    setBestQuiz(loadBestQuiz());
  }

  const readCount = Object.keys(readSet).length;
  const listenedCount = Object.keys(listenedSet).length;
  const juzCount = Object.values(juz).filter(Boolean).length;

  const tier = useMemo(() => {
    const pts = readCount + listenedCount + juzCount * 10 + bestQuiz * 2;
    return pts >= 50 ? 2 : pts >= 12 ? 1 : 0;
  }, [readCount, listenedCount, juzCount, bestQuiz]);

  const khatmaLeft = Math.max(0, 6236 - readCount);
  const khatmaDays = khatmaPerDay > 0 ? Math.ceil(khatmaLeft / khatmaPerDay) : 0;
  const khatmaPct = Math.min(100, Math.round((readCount / 6236) * 100));

  const submitAuth = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fName.trim()) return;
    const m: Member = {
      name: fName.trim(),
      email: fEmail.trim(),
      city: fCity.trim() || '—',
      joinedAt: Date.now(),
    };
    signIn(m);
  };

  const toggleJuz = (n: number) => {
    const next = { ...juz, [n]: !juz[n] };
    setJuz(next);
    saveHifzJuz(next);
  };

  const pick = (i: number) => {
    if (quizPick !== null) return;
    setQuizPick(i);
    const good = i === QUIZ[quizIdx].answer;
    const score = good ? quizScore + 1 : quizScore;
    setQuizScore(score);
    setTimeout(() => {
      setQuizPick(null);
      if (quizIdx + 1 >= QUIZ.length) {
        setQuizDone(true);
        if (score > bestQuiz) { setBestQuiz(score); saveBestQuiz(score); }
      } else setQuizIdx(quizIdx + 1);
    }, 900);
  };

  const resetQuiz = () => { setQuizIdx(0); setQuizScore(0); setQuizDone(false); setQuizPick(null); };

  const TierIcon = [Medal, Award, Crown][tier];
  const tierColor = ['text-[#b8c4b0]', 'text-[#f0d98c]', 'text-[#d4af37]'][tier];

  /* ============ IDENTIFICATION ============ */
  if (!member) {
    return (
      <section className="relative min-h-screen py-10" aria-label={t.espace.title}>
        {/* Bannière dorée — Espace Royal */}
        <PageBanner
          src="/images/banners/espace_banner.png"
          eyebrow={t.espace.eyebrow}
          title={t.espace.title}
          sub={t.espace.subtitle}
        />
        <div aria-hidden className="pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 55% 40% at 50% 0%, rgba(212,175,55,0.13), transparent 62%)' }} />
        <div className="relative mx-auto max-w-5xl px-4 pt-10 sm:px-6">

          <div className="grid gap-5 lg:grid-cols-[1.1fr_1fr]">
            {/* Carte d'identification */}
            <form onSubmit={submitAuth} className="glass-gold rounded-2xl p-6 sm:p-8">
              <h2 className="font-serif-royal text-2xl font-bold text-[#f0d98c]">{t.espace.signin_title}</h2>
              <p className="mt-1 text-xs text-[#cfe0cf]/65">{t.espace.signin_sub}</p>
              <div className="mt-5 space-y-4">
                {([
                  { label: t.espace.name, val: fName, set: setFName, ph: 'Muhammad Ndiaye', type: 'text' },
                  { label: t.espace.email, val: fEmail, set: setFEmail, ph: 'moi@exemple.com', type: 'email' },
                  { label: t.espace.city, val: fCity, set: setFCity, ph: 'Dakar · Touba · Paris…', type: 'text' },
                ] as const).map((f) => (
                  <label key={f.label} className="block">
                    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-[#cfe0cf]/70">{f.label}</span>
                    <input
                      type={f.type} required={f.label === t.espace.name}
                      value={f.val} onChange={(e) => f.set(e.target.value)} placeholder={f.ph}
                      className="w-full rounded-xl border border-[rgba(212,175,55,0.35)] bg-[rgba(2,25,19,0.8)] px-4 py-3 text-sm text-[#f5f0e1] placeholder:text-[#cfe0cf]/35 outline-none focus:border-[#d4af37]"
                    />
                  </label>
                ))}
              </div>
              <button type="submit" className="gold-glow-hover mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-[#e8c86a] to-[#b8933a] px-6 py-3.5 text-sm font-bold text-[#032019]">
                <ShieldCheck className="h-4 w-4" aria-hidden /> {t.espace.cta}
              </button>
              <p className="mt-3 text-center text-[10.5px] leading-relaxed text-[#cfe0cf]/45">{t.espace.privacy}</p>
            </form>

            {/* Avantages */}
            <div className="grid content-start gap-3">
              <div className="glass-gold rounded-2xl p-5">
                <h3 className="flex items-center gap-2 text-sm font-bold text-[#f0d98c]">
                  <Sparkles className="h-4 w-4" aria-hidden /> {t.espace.benefits_title}
                </h3>
                <ul className="mt-3 space-y-2.5">
                  {(t.espace.benefits as readonly string[]).map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-[13px] leading-snug text-[#e5e0d0]/85">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#7fd6b4]" aria-hidden />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="glass-gold rounded-2xl p-5">
                <h3 className="flex items-center gap-2 text-sm font-bold text-[#f0d98c]">
                  <Target className="h-4 w-4" aria-hidden /> {t.espace.tools_preview_title}
                </h3>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {([['khatma', BookOpenCheck], ['hifz', Brain], ['quiz', Target], ['progress', Sparkles]] as const).map(([k, Ic]) => (
                    <span key={k} className="flex items-center gap-2 rounded-lg border border-[rgba(212,175,55,0.25)] bg-[rgba(2,25,19,0.6)] px-3 py-2 text-[11.5px] font-semibold text-[#cfe0cf]/80">
                      <Ic className="h-3.5 w-3.5 text-[#d4af37]" aria-hidden />
                      {t.espace.tools[k as keyof typeof t.espace.tools]}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ============ TABLEAU DE BORD MEMBRE ============ */
  const initials = member.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <section className="relative min-h-screen py-10" aria-label={t.espace.title}>
      {/* Bannière dorée — tableau de bord */}
      <PageBanner
        src="/images/banners/espace_banner.png"
        eyebrow={t.espace.eyebrow}
        title={t.espace.title}
        height="sm"
      />
      <div aria-hidden className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 55% 40% at 50% 0%, rgba(212,175,55,0.12), transparent 62%)' }} />
      <div className="relative mx-auto max-w-6xl px-4 pt-10 sm:px-6">
        {/* Profil */}
        <div className="glass-gold mb-6 flex flex-wrap items-center gap-5 rounded-2xl p-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-[rgba(212,175,55,0.7)] bg-[rgba(212,175,55,0.12)] font-serif-royal text-xl font-bold text-[#f0d98c]">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="font-serif-royal truncate text-2xl font-bold text-[#f5f0e1]">{member.name}</h1>
            <p className="text-xs text-[#cfe0cf]/65">{member.email} · {member.city}</p>
            <p className="mt-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest">
              <span className={cn('flex items-center gap-1', tierColor)}><TierIcon className="h-4 w-4" /></span>
              <span className={tierColor}>{t.espace.tiers[tier]}</span>
              <span className="text-[#cfe0cf]/40">· {t.espace.since} {new Date(member.joinedAt).toLocaleDateString(uiLang === 'en' ? 'en-GB' : 'fr-FR')}</span>
            </p>
          </div>
          <button
            onClick={signOut}
            className="flex items-center gap-2 rounded-xl border border-[rgba(224,82,82,0.5)] bg-[rgba(224,82,82,0.1)] px-4 py-2 text-xs font-bold text-[#ffb3b3] transition-colors hover:bg-[rgba(224,82,82,0.2)]"
          >
            <LogOut className="h-3.5 w-3.5 rtl-flip" aria-hidden /> {t.espace.signout}
          </button>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          {/* ===== Khatma ===== */}
          <div className="glass-gold rounded-2xl p-6">
            <h2 className="flex items-center gap-2 text-base font-bold text-[#f0d98c]">
              <BookOpenCheck className="h-5 w-5 text-[#d4af37]" aria-hidden /> {t.espace.khatma_title}
            </h2>
            <p className="mt-1 text-xs text-[#cfe0cf]/60">{t.espace.khatma_sub}</p>
            <div className="mt-4 flex items-center gap-3">
              <input
                type="number" min={1} max={500} value={khatmaPerDay}
                onChange={(e) => setKhatmaPerDay(Math.max(1, Number(e.target.value) || 1))}
                className="w-24 rounded-lg border border-[rgba(212,175,55,0.35)] bg-[rgba(2,25,19,0.8)] px-3 py-2 text-center text-sm font-bold text-[#f0d98c] outline-none focus:border-[#d4af37]"
                aria-label={t.espace.per_day}
              />
              <span className="text-xs text-[#cfe0cf]/70">{t.espace.per_day}</span>
              <span className="royal-track ml-auto rounded-full px-3.5 py-1.5 text-xs font-bold text-[#032019]">
                {khatmaDays} {t.espace.days_left}
              </span>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-[11px] font-semibold text-[#cfe0cf]/60">
                <span>{readCount} / 6236 {t.espace.verses}</span>
                <span>{khatmaPct}%</span>
              </div>
              <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-[rgba(2,25,19,0.8)]">
                <div className="royal-track h-full rounded-full transition-all" style={{ width: `${Math.max(2, khatmaPct)}%` }} />
              </div>
              <p className="mt-2 text-[11px] text-[#cfe0cf]/50">{t.espace.khatma_note}</p>
            </div>
          </div>

          {/* ===== Hifz ===== */}
          <div className="glass-gold rounded-2xl p-6">
            <h2 className="flex items-center gap-2 text-base font-bold text-[#f0d98c]">
              <Brain className="h-5 w-5 text-[#d4af37]" aria-hidden /> {t.espace.hifz_title}
            </h2>
            <p className="mt-1 text-xs text-[#cfe0cf]/60">{t.espace.hifz_sub}</p>
            <div className="mt-4 grid grid-cols-10 gap-1.5">
              {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => toggleJuz(n)}
                  className={cn(
                    'aspect-square rounded-md border text-[11px] font-bold transition-all',
                    juz[n]
                      ? 'border-[#d4af37] bg-gradient-to-b from-[#e8c86a] to-[#b8933a] text-[#032019]'
                      : 'border-[rgba(212,175,55,0.3)] text-[#cfe0cf]/60 hover:border-[rgba(212,175,55,0.6)]'
                  )}
                  aria-pressed={!!juz[n]}
                  aria-label={`${t.espace.juz} ${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="mt-3 text-center text-xs font-bold text-[#7fd6b4]">
              {juzCount} / 30 {t.espace.juz_done}
            </p>
          </div>

          {/* ===== Quiz ===== */}
          <div className="glass-gold rounded-2xl p-6">
            <h2 className="flex items-center gap-2 text-base font-bold text-[#f0d98c]">
              <Target className="h-5 w-5 text-[#d4af37]" aria-hidden /> {t.espace.quiz_title}
            </h2>
            <p className="mt-1 text-xs text-[#cfe0cf]/60">
              {t.espace.best_score}: <span className="font-bold text-[#7fd6b4]">{bestQuiz} / {QUIZ.length}</span>
            </p>
            {quizDone ? (
              <div className="mt-5 text-center">
                <p className="font-serif-royal text-4xl font-bold text-[#f0d98c]">{quizScore} / {QUIZ.length}</p>
                <p className="mt-1 text-sm text-[#cfe0cf]/75">{quizScore >= 6 ? t.espace.quiz_bravo : t.espace.quiz_again}</p>
                <button onClick={resetQuiz} className="gold-glow-hover mt-4 rounded-xl bg-gradient-to-b from-[#e8c86a] to-[#b8933a] px-6 py-2.5 text-sm font-bold text-[#032019]">
                  {t.espace.quiz_retry}
                </button>
              </div>
            ) : (
              <div className="mt-4">
                <div className="flex items-center justify-between text-[11px] font-bold text-[#d4af37]">
                  <span>{t.espace.question} {quizIdx + 1} / {QUIZ.length}</span>
                  <span className="text-[#7fd6b4]">{quizScore} pts</span>
                </div>
                <p className="mt-2.5 min-h-[42px] text-sm font-semibold text-[#e5e0d0]/90">{QUIZ[quizIdx].q[bl]}</p>
                <div className="mt-3 space-y-2">
                  {QUIZ[quizIdx].options.map((o, i) => {
                    const isAnswer = i === QUIZ[quizIdx].answer;
                    const picked = quizPick === i;
                    return (
                      <button
                        key={i}
                        onClick={() => pick(i)}
                        disabled={quizPick !== null}
                        className={cn(
                          'flex w-full items-center justify-between rounded-lg border px-4 py-2.5 text-left text-[13px] font-semibold transition-all',
                          quizPick === null
                            ? 'border-[rgba(212,175,55,0.3)] text-[#cfe0cf]/85 hover:border-[rgba(212,175,55,0.65)] hover:text-[#f0d98c]'
                            : isAnswer
                              ? 'border-[#2e8b6e] bg-[rgba(46,139,110,0.2)] text-[#7fd6b4]'
                              : picked
                                ? 'border-[#e05252] bg-[rgba(224,82,82,0.14)] text-[#e08a8a]'
                                : 'border-[rgba(212,175,55,0.18)] text-[#cfe0cf]/50'
                        )}
                      >
                        {o[bl]}
                        {quizPick !== null && isAnswer && <Check className="h-4 w-4" aria-hidden />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ===== Progression + accès rapides ===== */}
          <div className="space-y-5">
            <div className="glass-gold rounded-2xl p-6">
              <h2 className="flex items-center gap-2 text-base font-bold text-[#f0d98c]">
                <Sparkles className="h-5 w-5 text-[#d4af37]" aria-hidden /> {t.espace.progress_title}
              </h2>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                {[
                  { v: readCount, l: t.espace.read_verses },
                  { v: listenedCount, l: t.espace.listened_verses },
                  { v: bookmarks.length, l: t.espace.bookmarks },
                ].map((s) => (
                  <div key={s.l} className="rounded-xl border border-[rgba(212,175,55,0.25)] bg-[rgba(2,25,19,0.65)] px-2 py-4">
                    <p className="font-serif-royal text-3xl font-bold text-[#f0d98c]">{s.v}</p>
                    <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#cfe0cf]/60">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass-gold rounded-2xl p-6">
              <h2 className="text-sm font-bold uppercase tracking-widest text-[#d4af37]">{t.espace.quick_title}</h2>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {([
                  { key: 'tools', icon: <Headphones className="h-4 w-4" /> },
                  { key: 'qibla', icon: <Compass className="h-4 w-4" /> },
                  { key: 'hijri', icon: <CalendarDays className="h-4 w-4" /> },
                  { key: 'conference', icon: <Video className="h-4 w-4" /> },
                  { key: 'sciences', icon: <BookOpenCheck className="h-4 w-4" /> },
                  { key: 'univers', icon: <Globe2 className="h-4 w-4" /> },
                ] as const).map((it) => (
                  <QuickLink key={it.key} to={it.key} label={t.nav[it.key as keyof typeof t.nav]} icon={it.icon} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function QuickLink({ to, label, icon }: { to: Parameters<ReturnType<typeof useApp.getState>['setSection']>[0]; label: string; icon: React.ReactNode }) {
  const setSection = useApp((s) => s.setSection);
  return (
    <button
      onClick={() => setSection(to)}
      className="group flex items-center justify-between rounded-lg border border-[rgba(212,175,55,0.25)] bg-[rgba(2,25,19,0.6)] px-3 py-2.5 text-[12px] font-bold text-[#cfe0cf]/85 transition-all hover:border-[rgba(212,175,55,0.6)] hover:text-[#f0d98c]"
    >
      <span className="flex items-center gap-2">
        <span className="text-[#d4af37]">{icon}</span>
        {label}
      </span>
      <ChevronRight className="h-3.5 w-3.5 text-[#d4af37]/50 transition-transform group-hover:translate-x-0.5 rtl-flip" aria-hidden />
    </button>
  );
}
